import { type ViolationTypeDetail } from '@aysar/contracts';

import { toApiError } from '@/shared/utils/transform-error/transform-error';
import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

import { useViolationTypeQuery } from '../../services/violations/violations.api';
import {
  consequenceCopy,
  type ConsequenceCopy,
} from '../../utils/violation-consequence/violation-consequence.util';

export interface UseViolationConsequenceLogic {
  type: ViolationTypeDetail | null;
  /** `null` until a rule has been chosen and read. */
  copy: ConsequenceCopy | null;
  isLoading: boolean;
  /** The rule could not be read — the confirmation must not promise anything. */
  error: ApiError | null;
  reload: () => void;
}

/**
 * What confirming this rule will actually do, read from the rule ITSELF.
 *
 * ## It re-reads the rule rather than trusting the case row
 *
 * `ViolationDetail` carries `configuredAction` but not the suspension
 * length, and "Suspend" without "for how long" is not a consequence anybody can
 * weigh. More importantly, `EnforcementService` reads the rule **at the moment
 * it acts** — that is the whole of P20's "changing a type's action in admin
 * changes behaviour without deploy" — so a dialog quoting a stale copy of the
 * action would promise one thing while the api did another. Reading the live
 * rule is what makes the promise true.
 *
 * ## It is free to read
 *
 * `GET /violation-types/:id` answers to `violations.manage`, which is exactly
 * the permission the confirmation itself requires — so no operator ever reaches
 * this and is refused. And the queue's own catalogue reads share the cache.
 */
export function useViolationConsequenceLogic(
  violationTypeId: string | null,
): UseViolationConsequenceLogic {
  const query = useViolationTypeQuery(violationTypeId ?? '', {
    skip: violationTypeId === null,
  });

  const type = query.data ?? null;

  return {
    type,
    copy: type === null ? null : consequenceCopy(type.action, type.suspensionDays),
    isLoading: query.isLoading,
    error: query.error === undefined ? null : toApiError(query.error),
    reload: () => {
      void query.refetch();
    },
  };
}
